// eslint-disable-next-line wandb/no-deprecated-imports
import {IconButton} from '@wandb/components';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {ApolloQueryResult} from 'apollo-client';
import classNames from 'classnames';
import React, {useContext, useMemo, useState} from 'react';
import {useHistory} from 'react-router';

import {envIsPublicCloud} from '../../config';
import {useAutoRefreshSettings} from '../../containers/AutoRefreshSettings';
import {
  Maybe,
  ProjectArtifactsQuery,
  useArtifactProjectMetadataSearchLazyQuery,
} from '../../generated/graphql';
import * as Generated from '../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {ArtifactCollection} from '../../state/graphql/projectArtifactsQuery';
import {propagateErrorsContext} from '../../util/errors';
import {useDebounceState} from '../../util/hooks';
import {artifactSequence} from '../../util/urls';
import {usePaginate} from '../../util/usePaginate';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {CreateArtifactModal} from '../CreateArtifactCollectionModal';
import {useIsGlobalRegistryEnabled} from '../Registries/common/hooks';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {InstrumentedLoader as Loader} from '../utility/InstrumentedLoader';
import * as S from './ArtifactSidebar.styles';
import {
  ArtifactSidebarPaginate,
  ArtifactSidebarSequenceWithUrlToggle,
} from './ArtifactSidebarSequence';
import {MetadataFilterPopup} from './MetadataFilterPopup';
import {FilterInfo, FilterValueTypes} from './MetadataFilterRow';
import {ArtifactType} from './MetadataQueryResultsTypes';
import {
  ARTIFACT_POLL_INTERVAL,
  ARTIFACTS_PER_TYPE,
  ArtifactSideBarDataSearch,
  collectionNameFilterToMongoFilter,
  compareFiltersEqual,
  formatArtifactMetadataSearchQuery,
} from './utils';

export const automaticallyGeneratedTypeNames = new Set(['run_table', 'code']);
const standardMLTypeNames = new Set(['model', 'dataset']);

export const artifactTypeNameSortFn = (a: string, b: string): number => {
  if (standardMLTypeNames.has(a) && !standardMLTypeNames.has(b)) {
    return -1;
  }
  if (standardMLTypeNames.has(b) && !standardMLTypeNames.has(a)) {
    return 1;
  }
  if (
    automaticallyGeneratedTypeNames.has(a) &&
    !automaticallyGeneratedTypeNames.has(b)
  ) {
    return 1;
  }
  if (
    !automaticallyGeneratedTypeNames.has(a) &&
    automaticallyGeneratedTypeNames.has(b)
  ) {
    return -1;
  }
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

interface ArtifactSidebarTypeProps {
  id?: string;
  name: string;
  entityName: string;
  projectName: string;
  artifactTypeName?: string;
  artifactCollectionName?: string;
  artifactCollections?: Array<
    Maybe<Pick<ArtifactCollection, 'id' | 'name'>> | undefined
  >;
  artifactCommitHash?: string;
  artifactTab?: string;
  filePath?: string;
  refetchProjectArtifacts: () => Promise<
    ApolloQueryResult<ProjectArtifactsQuery>
  >;
}

const ArtifactSidebarType: React.FC<ArtifactSidebarTypeProps> = props => {
  const {
    id,
    name,
    artifactCollections,
    artifactTypeName,
    artifactCollectionName,
  } = props;

  const filteredSortedArtifactCollections = React.useMemo(() => {
    const filteredCollections = (artifactCollections?.filter(s => s != null) ??
      []) as ArtifactCollection[];
    return [
      ...filteredCollections.filter(s => s.__typename === 'ArtifactPortfolio'),
      ...filteredCollections.filter(s => s.__typename !== 'ArtifactPortfolio'),
    ];
  }, [artifactCollections]);

  const {
    totalPages,
    hasNextPage,
    hasPreviousPage,
    setPage,
    total,
    page,
    itemsPage,
  } = usePaginate(filteredSortedArtifactCollections, ARTIFACTS_PER_TYPE);

  const [active, setActive] = useState(
    artifactTypeName === name || !automaticallyGeneratedTypeNames.has(name)
  );
  const iconToggle = active ? 'chevron-down' : 'chevron-next';

  return (
    <Tailwind>
      <S.ArtifactTypeContainer key={id} className="artifact-type">
        <S.ArtifactTypeBox onClick={() => setActive(s => !s)}>
          <Icon name={iconToggle} />
          <span className="artifact-type-title inline-block truncate">
            {name}
          </span>
        </S.ArtifactTypeBox>
        {active && (
          <S.ArtifactsBox>
            {itemsPage.map(a => (
              <ArtifactSidebarSequenceWithUrlToggle
                key={a.id}
                {...props}
                {...a}
                collectionTypeName={a.__typename}
                artifactCollectionName={artifactCollectionName}
                activeArtifactTypeName={artifactTypeName}
                artifactTypeName={name}
              />
            ))}
            {totalPages > 0 && (
              <S.ArtifactSidebarTypePaginate>
                <ArtifactSidebarPaginate
                  size="tiny"
                  perPage={ARTIFACTS_PER_TYPE}
                  page={page}
                  total={total}
                  pageInfo={{hasNextPage, hasPreviousPage}}
                  onNextClick={() => setPage(s => s + 1)}
                  onPreviousClick={() => setPage(s => s - 1)}
                />
              </S.ArtifactSidebarTypePaginate>
            )}
          </S.ArtifactsBox>
        )}
      </S.ArtifactTypeContainer>
    </Tailwind>
  );
};

interface ArtifactSidebarProps {
  entityName: string;
  projectName: string;
  artifactTypeName?: string;
  artifactCollectionName?: string;
  artifactCommitHash?: string;
  artifactTab?: string;
  filePath?: string;
}

const ArtifactSidebar: React.FC<ArtifactSidebarProps> = props => {
  const {autoRefreshEnabled} = useAutoRefreshSettings();
  const pollInterval = autoRefreshEnabled ? ARTIFACT_POLL_INTERVAL : 0;
  const {selectedAccount} = useContext(AccountSelectorContext);
  const accountOrgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const history = useHistory();
  const [focused, setFocused] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [openFilterPopup, setOpenFilterPopup] = useState(false);
  const [searchQuery, searchQueryDebounced, setSearchQuery] = useDebounceState(
    '',
    250
  );
  const {data, refetch} = Generated.useProjectArtifactsQuery({
    pollInterval,
    variables: {
      entityName: props.entityName,
      projectName: props.projectName,
      filters: searchQueryDebounced
        ? collectionNameFilterToMongoFilter(searchQueryDebounced)
        : undefined,
    },
  });
  const artifactTypes = data?.project?.artifactTypes;

  const filteredArtifactTypes = useMemo(() => {
    return (artifactTypes?.edges ?? [])
      .filter(at => at.node != null)
      .map(at => ({
        ...at.node!,
        artifactCollections: (at?.node?.artifactCollections?.edges ?? [])
          .map(e => e.node)
          .filter(seq => seq != null),
      }))
      .sort((a, b) => artifactTypeNameSortFn(a.name, b.name));
  }, [artifactTypes]);

  const afterCreateArtifact = React.useCallback<
    React.ComponentProps<typeof CreateArtifactModal>['afterCreate']
  >(
    ({
      entityName,
      projectName,
      artifactTypeName,
      artifactName: artifactSequenceName,
    }) => {
      refetch().then(() => {
        history.push(
          artifactSequence({
            entityName,
            projectName,
            artifactTypeName,
            artifactSequenceName,
          })
        );
        setCreateModalOpen(false);
      });
    },
    [history, refetch]
  );

  const initialFilterState: FilterInfo[] = [
    {
      id: Math.random(),
      type: FilterValueTypes.String,
      key: undefined,
      value: undefined,
    },
  ];

  const [errorFetchingMetadataQuery, setErrorFetchingMetadataQuery] =
    useState(false);

  const [currentFilterRows, setCurrentFilterRows] =
    useState(initialFilterState);
  const [savedFilterRows, setSavedFilterRows] = useState(initialFilterState);

  const artifactMetadataSearchVariables: Generated.ArtifactProjectMetadataSearchQueryVariables =
    {
      entityName: props.entityName,
      projectName: props.projectName,
      filters: '',
    };

  const [formattedMetadataSearchQuery, setFormattedMetadataSearchQuery] =
    useState<ArtifactSideBarDataSearch | undefined>(undefined);

  const [
    getMetadataSearchArtifacts,
    {loading: metadataSearchLoading, error: metadataSearchError},
  ] = useArtifactProjectMetadataSearchLazyQuery({
    variables: artifactMetadataSearchVariables,
    context: propagateErrorsContext(),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: fetchedData => {
      const formattedQuery = formatArtifactMetadataSearchQuery(fetchedData);
      setFormattedMetadataSearchQuery(formattedQuery);
    },
  });

  const {enableGlobalRegistry, enableGlobalRegistryLoading} =
    useIsGlobalRegistryEnabled(props.entityName);

  if (enableGlobalRegistryLoading) {
    return null;
  }

  if (metadataSearchLoading) {
    return <Loader name="artifact-project-metadata-search" samplingRate={1} />;
  }

  const hasMetadataSearchFilter = () =>
    !compareFiltersEqual(initialFilterState, savedFilterRows);
  const hasErrorMetadataSearch = () =>
    errorFetchingMetadataQuery ||
    (metadataSearchError && hasMetadataSearchFilter);
  const hasMetadataSearchResults = () => formattedMetadataSearchQuery != null;

  return (
    <Tailwind style={{width: '100%', height: '100%'}}>
      <S.ArtifactSidebarWrapper>
        <S.ArtifactSidebarTitle>Artifacts</S.ArtifactSidebarTitle>
        <S.ArtifactSidebarHeader>
          <S.ArtifactSearchBar
            className={classNames({
              'artifact-sidebar-search-bar-focus': focused,
            })}
            icon={{className: 'wbic-ic-search', size: 'large'}}
            iconPosition="left"
            onChange={(_, {value}) => setSearchQuery(value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(searchQuery.length > 0)}
            placeholder={'Find matching artifacts'}
          />
          <MetadataFilterPopup
            open={openFilterPopup}
            setOpen={setOpenFilterPopup}
            initialFilterState={initialFilterState}
            currentFilterRows={currentFilterRows}
            setCurrentFilterRows={setCurrentFilterRows}
            savedFilterRows={savedFilterRows}
            setSavedFilterRows={setSavedFilterRows}
            getMetadataSearchArtifacts={getMetadataSearchArtifacts}
            artifactMetadataSearchVariables={artifactMetadataSearchVariables}
            setErrorFetchingMetadataQuery={setErrorFetchingMetadataQuery}
          />
        </S.ArtifactSidebarHeader>
        <S.ArtifactSidebarContents>
          {hasErrorMetadataSearch() ? (
            <div className="font-source-sans-pro py-8 text-base font-normal leading-6 text-red-550">
              Error due to filter settings.
            </div>
          ) : hasMetadataSearchFilter() && hasMetadataSearchResults() ? (
            <div>
              {Object.entries(formattedMetadataSearchQuery!.artifactTypes).map(
                ([typeName, sequences]) => (
                  <ArtifactType
                    key={typeName}
                    entityName={props.entityName}
                    projectName={props.projectName}
                    typeName={typeName}
                    sequences={sequences}
                    refetchProjectArtifacts={refetch}
                    artifactFilterResults={searchQueryDebounced}
                    artifactCollectionName={props.artifactCollectionName}
                  />
                )
              )}
            </div>
          ) : hasMetadataSearchFilter() && !hasMetadataSearchResults() ? (
            <div className="font-source-sans-pro py-8 text-base font-normal leading-6 text-moon-500">
              No artifacts found
            </div>
          ) : (
            filteredArtifactTypes
              .filter(({artifactCollections}) => artifactCollections.length > 0)
              .map(artifact => (
                <ArtifactSidebarType
                  key={artifact.id}
                  {...props}
                  {...artifact}
                  refetchProjectArtifacts={refetch}
                />
              ))
          )}
        </S.ArtifactSidebarContents>
        {hasMetadataSearchFilter() && (
          <div className="font-source-sans-pro w-full p-8 text-moon-500">
            <div>Maximum 150k artifacts queried.</div>
            <div>Maximum 10k results returned.</div>
          </div>
        )}
        <S.ArtifactSidebarFooter>
          <IconButton
            size="sm"
            aria-label="Add new artifact"
            variant="ghost"
            icon="add-new"
            data-cy="artifact-add"
            onPress={() => setCreateModalOpen(true)}
          />
          <CreateArtifactModal
            entityName={props.entityName}
            projectName={props.projectName}
            open={createModalOpen}
            onCloseAction={() => setCreateModalOpen(false)}
            afterCreate={afterCreateArtifact}
          />
        </S.ArtifactSidebarFooter>
      </S.ArtifactSidebarWrapper>
    </Tailwind>
  );
};

export default ArtifactSidebar;
